import React, {useState, useEffect, useCallback, JSX} from 'react';
import UserFormModal from './UserFormModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCertificate, faIdBadge, faPencil, faTrash } from '@awesome.me/kit-e6334c76d4/icons/duotone/solid';
import './UserManagementPage.css';
import { apiFetch } from '../../services/api';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination, IconButton, Typography, Button, Collapse, Box
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { useOri } from '../../context/OriContext';
import LoadingIcon from '../LoadingIcon';

class ApiError extends Error {
    constructor(message: string, public status: number, public code: string) {
        super(message);
        this.name = 'ApiError';
    }
}

const roleIconMap: { [key: string]: JSX.Element } = {
    'site-admin': <FontAwesomeIcon icon={faCertificate} title="site-admin" />,
    'org-admin': <FontAwesomeIcon icon={faIdBadge} title="org-admin" />,
};

export const isCurrentUserSiteAdmin = (): boolean => {
    const groupsStr = sessionStorage.getItem('groups');
    if (!groupsStr) return false;
    try {
        return JSON.parse(groupsStr).includes('site-admin');
    } catch {
        return false;
    }
};

export interface User {
    username: string;
    status?: "CONFIRMED" | "UNCONFIRMED" | "ARCHIVED" | "COMPROMISED" | "UNKNOWN" | "RESET_REQUIRED" | "FORCE_CHANGE_PASSWORD";
    enabled?: boolean;
    created?: string;
    lastModified?: string;
    attributes: {
        email: string;
        given_name?: string;
        family_name?: string;
        email_verified?: string;
        'custom:agency_ori'?: string;
        sub?: string;
    };
    groups?: string[]; // Groups may not be in the list response, but needed for editing
}

const fetchUsersApi = async (ori: string): Promise<User[]> => {
    const response = await apiFetch(`/api/risc/users?ori=${ori}`);
    if (!response.ok) throw new Error('Failed to fetch users');
    return response.json();
};

const saveUserApi = async (user: User, isNew: boolean, originalUser: User | null, ori: string) => {
    if (isNew) {
        // Handle user creation (POST to /api/risc/users)
        const payload = {
            username: user.username,
            email: user.attributes.email,
            groups: user.groups,
            firstName: user.attributes.given_name,
            lastName: user.attributes.family_name,
            agencyOri: user.attributes['custom:agency_ori'] || ori,
            ...((user as any).password && { password: (user as any).password }),
        };
        const response = await apiFetch('/api/risc/users', {
            method: 'POST',
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({})); // Gracefully handle non-JSON responses
            throw new ApiError(errorData.message || 'Failed to create user', response.status, errorData.error);
        }
        return response.json();
    }

    // Handle user update (PUT for attributes, POST/DELETE for groups)
    const attributePayload: { [key: string]: any } = {};
    if (originalUser) {
        if (user.attributes.email !== originalUser.attributes.email) {
            attributePayload.email = user.attributes.email;
        }
        if (user.attributes.given_name !== originalUser.attributes.given_name) {
            attributePayload.firstName = user.attributes.given_name;
        }
        if (user.attributes.family_name !== originalUser.attributes.family_name) {
            attributePayload.lastName = user.attributes.family_name;
        }
        if (user.attributes['custom:agency_ori'] !== originalUser.attributes['custom:agency_ori']) {
            attributePayload.agencyOri = user.attributes['custom:agency_ori'];
        }
    }

    // Only make the PUT call if there are attributes to update
    if (Object.keys(attributePayload).length > 0) {
        const response = await apiFetch(`/api/risc/users/${user.username}?ori=${ori}`, {
            method: 'PUT',
            body: JSON.stringify(attributePayload)
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new ApiError(errorData.message || 'Failed to update user', response.status, errorData.error);
        }
    }

    // Handle group changes
    const newGroups = user.groups || [];
    const originalGroups = originalUser?.groups || [];
    const addedGroups = newGroups.filter(g => !originalGroups.includes(g));
    const removedGroups = originalGroups.filter(g => !newGroups.includes(g));

    for (const groupName of addedGroups) {
        await apiFetch(`/api/risc/users/${user.username}/groups?ori=${ori}`, {
            method: 'POST', body: JSON.stringify({ groupName })
        });
    }
    for (const groupName of removedGroups) {
        await apiFetch(`/api/risc/users/${user.username}/groups/${groupName}?ori=${ori}`, {
            method: 'DELETE'
        });
    }
};

const deleteUserApi = async (username: string, ori: string) => {
    const response = await apiFetch(`/api/risc/users/${username}?ori=${ori}`, { method: 'DELETE' });
    if (!response.ok) throw new Error('Failed to delete user');
}

const Row: React.FC<{ user: User, onEdit: (user: User) => void, onDelete: (username: string) => void }> = ({ user, onEdit, onDelete }) => {
    const [open, setOpen] = useState(false);

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell className="mobile-only" sx={{ width: '10px', padding: '0' }}>
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                </TableCell>
                <TableCell component="th" scope="row">
                    {user.username}
                </TableCell>
                <TableCell className="desktop-only">{[user.attributes.family_name, user.attributes.given_name].filter(Boolean).join(', ')}</TableCell>
                <TableCell className="desktop-only">{user.attributes.email}</TableCell>
                <TableCell className="desktop-only">
                    {user.groups?.map(group => (
                        <span key={group} className="role-icon" title={group}>
                            {roleIconMap[group] || group}
                        </span>
                    ))}
                </TableCell>
                <TableCell className="desktop-only">
                    <span className={`status-badge status-${user.status?.toLowerCase()}`}>{user.status}</span>
                </TableCell>
                <TableCell align="right">
                    <IconButton onClick={() => onEdit(user)} title="Edit User">
                        <FontAwesomeIcon icon={faPencil} swapOpacity />
                    </IconButton>
                    <IconButton onClick={() => onDelete(user.username)} title="Delete User" color="warning">
                        <FontAwesomeIcon icon={faTrash} swapOpacity />
                    </IconButton>
                </TableCell>
            </TableRow>
            <TableRow className="mobile-only">
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={3}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            <Typography variant="subtitle1" gutterBottom component="div">
                                Details
                            </Typography>
                            <Table size="small" aria-label="user details">
                                <TableBody>
                                    <TableRow>
                                        <TableCell component="th" scope="row">Name</TableCell>
                                        <TableCell>{[user.attributes.family_name, user.attributes.given_name].filter(Boolean).join(', ')}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell component="th" scope="row">Email</TableCell>
                                        <TableCell>{user.attributes.email}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell component="th" scope="row">Roles</TableCell>
                                        <TableCell>
                                            {user.groups?.map(group => (
                                                <span key={group} className="role-icon" title={group}>
                                                    {roleIconMap[group] || group}
                                                </span>
                                            ))}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell component="th" scope="row">Status</TableCell>
                                        <TableCell>
                                            <span className={`status-badge status-${user.status?.toLowerCase()}`}>{user.status}</span>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
};

const UserManagementPage: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [modalError, setModalError] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const { selectedOri } = useOri();

    const fetchUsers = useCallback(async () => {
        if (!selectedOri) return;
        setIsLoading(true);
        try {
            const usersData = await fetchUsersApi(selectedOri);
            setUsers(usersData);
        } catch (err) {
            setError('Failed to fetch users.');
        } finally {
            setIsLoading(false);
        }
    }, [selectedOri]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers, selectedOri]);

    const handleAddUser = () => {
        setCurrentUser(null);
        setModalError(null);
        setIsModalOpen(true);
    };

    const handleEditUser = (user: User) => {
        setCurrentUser(user);
        setModalError(null);
        setIsModalOpen(true);
    };

    const handleDeleteUser = async (username: string) => {
        if (window.confirm(`Are you sure you want to delete user "${username}"?`)) {
            if (!selectedOri) return;
            try {
                await deleteUserApi(username, selectedOri);
                await fetchUsers(); // Re-fetch users to update the list
            } catch (err) {
                setError(`Failed to delete user ${username}.`);
            }
        }
    };

    const handleSaveUser = async (user: User & { password?: string }) => {
        if (!selectedOri) return;
        try {
            setModalError(null);
            await saveUserApi(user, !currentUser, currentUser, selectedOri);
            setIsModalOpen(false);
            await fetchUsers(); // Re-fetch users
        } catch (err) {
            if (err instanceof ApiError && err.status === 400 && err.code === 'INVALID_PASSWORD') {
                setModalError(err.message);
            } else {
                setError('Failed to save user.');
                setIsModalOpen(false);
            }
        }
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <>
            <LoadingIcon isLoading={isLoading} showOverlay={true} />
            <Paper sx={{ p: 2, m: 1 }}>
                <div className="user-management-header">
                <Typography variant="h6" gutterBottom component="div">
                    User Management
                </Typography>
                <Button variant="contained" onClick={handleAddUser}>Add User</Button>
                </div>
                {error && <p className="error-message">{error}</p>}
                {!isLoading && !error && (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell className="mobile-only" />
                                    <TableCell>Username</TableCell>
                                    <TableCell className="desktop-only">Name</TableCell>
                                    <TableCell className="desktop-only">Email Address</TableCell>
                                    <TableCell className="desktop-only">Roles</TableCell>
                                    <TableCell className="desktop-only">Status</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(user => (
                                    <Row key={user.username} user={user} onEdit={handleEditUser} onDelete={handleDeleteUser} />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={users.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
            {isModalOpen && (
                <UserFormModal
                    user={currentUser}
                    onClose={() => setIsModalOpen(false)}
                    onSave={handleSaveUser}
                    error={modalError}
                />
            )}
        </>
    );
};

export default UserManagementPage;
